import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { RdLocalizedNumberPipe } from '../../../pipes/rd-localized-number.pipe';

/**
 * PropertyAnalysis Widget
 */
@Component({
  selector: 'property-analysis-widget',
  templateUrl: './property-analysis-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './property-analysis-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyAnalysisWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
  * Constructor
  * @param rdLocalizedNumberPipe RdLocalizedNumberPipe
  */
  constructor(injector: Injector, private rdLocalizedNumberPipe: RdLocalizedNumberPipe) {
    super(injector, 'propertyAnalysis', true);
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Setup widget's layout if required
   */
  setupLayouts() {
    const fixLayouts = this.availableLayouts.length === 0 && this.currentLayout === 'default';

    if (fixLayouts) {
      this.currentLayout = 'Large';
    }
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("propertyAnalysis", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.setupLayouts();
    this.mockupData$ = this.widgetService.loadMockup('propertyAnalysis').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('propertyAnalysis', this.getReport()).pipe();
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;

        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
      this.ref.markForCheck();
    } catch (e) {
      // Nothing
    }
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    data.distributionOfLettableArea = [];
    data.distributionOfRentalIncome = [];

    let areaSum = 0;
    let rentSum = 0;

    for (const entry of data.units) {
      const chartEntryLettableArea = { label: entry.areaType, value: entry.lettableArea };
      const chartEntryRentalIncome = { label: entry.areaType, value: entry.contractualRentYear };

      areaSum += entry.lettableArea;
      rentSum += entry.contractualRentYear;

      data.distributionOfLettableArea.push(chartEntryLettableArea);
      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.parking) {
      const chartEntryRentalIncome = { label: entry.unitType, value: entry.contractualRentYear };
      rentSum += entry.contractualRentYear;

      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.externalArea) {
      const chartEntryLettableArea = { label: entry.areaType, value: entry.lettableArea };
      const chartEntryRentalIncome = { label: entry.areaType, value: entry.contractualRentYear };

      areaSum += entry.lettableArea;
      rentSum += entry.contractualRentYear;

      data.distributionOfLettableArea.push(chartEntryLettableArea);
      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.distributionOfLettableArea) {
      entry.percentage = this.rdLocalizedNumberPipe.transform(entry.value / areaSum * 100, 1, false, '%', '', 0);
    }

    for (const entry of data.distributionOfRentalIncome) {
      entry.percentage = this.rdLocalizedNumberPipe.transform(entry.value / rentSum * 100, 1, false, '%', '', 0);
    }

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Checks if value is valid year
   * @param value {any} Value
   */
  private isValidYear(value) {
    return !(value == null || value == "" || value == "0");
  }

  /**
   * Returns chart Area label content
  * @param e {any} Event object
   */
  chartLabelContentArea(e: any): string {
    return `${e.dataItem.percentage}`;
  }

  /**
   * Returns chart Rent label content
  * @param e {any} Event object
   */
  chartLabelContentRent(e: any): string {
    return `${e.dataItem.percentage}`;
  }

  /**
   * Returns formatted YearOfConstruction
   */
  getYearOfConstructionFormatted(): string {
    const value = this.widgetData.yearOfConstruction;
    const value2 = this.widgetData.yearOfConstruction2;
    const value3 = this.widgetData.yearOfConstruction3;
    const value4 = this.widgetData.yearOfConstruction4;
    const value5 = this.widgetData.yearOfConstruction5;

    if (!this.isValidYear(value)) {
      return this.reportDesignerTranslator.getString('common.notAvailable');
    } else {
      let str = "";
      str += (this.isValidYear(value5) ? (value5 + " / ") : "");
      str += (this.isValidYear(value4) ? (value4 + " / ") : "");
      str += (this.isValidYear(value3) ? (value3 + " / ") : "");
      str += (this.isValidYear(value2) ? (value2 + " / ") : "");
      str += (this.isValidYear(value) ? (value) : "");

      return str;
    }
  }

  /**
   * Returns formatted YearOfRefurbishment
   */
  getYearOfRefurbishmentFormatted(): string {
    const value = this.widgetData.yearOfRefurbishment;
    const value2 = this.widgetData.yearOfRefurbishment2;
    const value3 = this.widgetData.yearOfRefurbishment3;
    const value4 = this.widgetData.yearOfRefurbishment4;
    const value5 = this.widgetData.yearOfRefurbishment5;

    if (!this.isValidYear(value)) {
      return this.reportDesignerTranslator.getString('common.notAvailable');
    } else {
      let str = "";
      str += (this.isValidYear(value5) ? (value5 + " / ") : "");
      str += (this.isValidYear(value4) ? (value4 + " / ") : "");
      str += (this.isValidYear(value3) ? (value3 + " / ") : "");
      str += (this.isValidYear(value2) ? (value2 + " / ") : "");
      str += (this.isValidYear(value) ? (value) : "");

      return str;
    }
  }
}
